import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

interface BackButtonProps {
  fallbackPath?: string;
  label?: string;
  className?: string;
  showOnPaths?: string[];
}

const BackButton: React.FC<BackButtonProps> = ({ 
  fallbackPath = '/dashboard',
  label = 'Back',
  className,
  showOnPaths
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Don't show on dashboard or root
  if (location.pathname === '/' || location.pathname === '/dashboard') {
    return null;
  }

  // If showOnPaths is specified, only show on those paths
  if (showOnPaths && !showOnPaths.some(path => location.pathname.startsWith(path))) {
    return null;
  }

  const handleBack = () => {
    // Check if there's history to go back to
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(fallbackPath);
    }
  };

  return (
    <button
      onClick={handleBack}
      className={clsx(
        'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
        'text-gray-700 dark:text-gray-300',
        'bg-white dark:bg-gray-800',
        'border border-gray-300 dark:border-gray-600',
        'hover:bg-gray-50 dark:hover:bg-gray-700',
        'hover:border-gray-400 dark:hover:border-gray-500',
        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
        'shadow-sm hover:shadow-md',
        className
      )}
      aria-label="Go back"
    >
      <ArrowLeftIcon className="h-4 w-4" />
      <span>{label}</span>
    </button>
  );
};

export default BackButton;
